'use client'

import Cookies from 'js-cookie'
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-plusplus */
import { useEffect, useRef } from 'react'

import { useToast } from '@/components/ui'
import { AUTH_COOKIE, USER_INFO_COOKIE } from '@/utils/constants'

// Variáveis globais para persistir entre remontagens
let globalWarningTimer: NodeJS.Timeout | null = null
let globalLogoutTimer: NodeJS.Timeout | null = null
let globalWarningToastId: string | null = null
let globalLogoutToastId: string | null = null
let globalIsInitialized = false
let globalToastFunction: ((params: any) => { id: string }) | null = null
let globalDismissFunction: ((id: string) => void) | null = null
let globalDebounceTimer: NodeJS.Timeout | null = null

// Função global para resetar timers com debounce
const globalResetInactivityTimer = () => {
  // Debounce para evitar múltiplas chamadas
  if (globalDebounceTimer) {
    clearTimeout(globalDebounceTimer)
  }

  globalDebounceTimer = setTimeout(() => {
    // Limpa timers e toasts existentes
    if (globalWarningTimer) {
      clearTimeout(globalWarningTimer)
      globalWarningTimer = null
    }
    if (globalLogoutTimer) {
      clearTimeout(globalLogoutTimer)
      globalLogoutTimer = null
    }

    if (globalWarningToastId && globalDismissFunction) {
      globalDismissFunction(globalWarningToastId)
      globalWarningToastId = null
    }
    if (globalLogoutToastId && globalDismissFunction) {
      globalDismissFunction(globalLogoutToastId)
      globalLogoutToastId = null
    }

    // Configura novos timers
    const timeToTriggerAlertInSeconds = 60 * 30 // 30 minutos
    const timeToTriggerLogoutInSeconds = timeToTriggerAlertInSeconds + 60

    // Timer para mostrar aviso
    globalWarningTimer = setTimeout(() => {
      if (globalToastFunction) {
        const toastResult = globalToastFunction({
          title: 'Você será deslogado em 60 segundos por inatividade',
          variant: 'warning',
        })
        globalWarningToastId = toastResult.id
      }
    }, timeToTriggerAlertInSeconds * 1000)

    // Timer para logout
    globalLogoutTimer = setTimeout(() => {
      // Remove toast de aviso
      if (globalWarningToastId && globalDismissFunction) {
        globalDismissFunction(globalWarningToastId)
        globalWarningToastId = null
      }

      // Mostra toast de logout
      if (globalToastFunction) {
        const toastResult = globalToastFunction({
          title: 'Você está sendo deslogado por inatividade',
          variant: 'warning',
        })
        globalLogoutToastId = toastResult.id
      }

      // Executa logout após 2 segundos
      setTimeout(() => {
        // Limpa tudo
        if (globalWarningTimer) {
          clearTimeout(globalWarningTimer)
          globalWarningTimer = null
        }
        if (globalLogoutTimer) {
          clearTimeout(globalLogoutTimer)
          globalLogoutTimer = null
        }
        if (globalDebounceTimer) {
          clearTimeout(globalDebounceTimer)
          globalDebounceTimer = null
        }
        if (globalWarningToastId && globalDismissFunction) {
          globalDismissFunction(globalWarningToastId)
          globalWarningToastId = null
        }
        if (globalLogoutToastId && globalDismissFunction) {
          globalDismissFunction(globalLogoutToastId)
          globalLogoutToastId = null
        }

        Cookies.remove(AUTH_COOKIE)
        Cookies.remove(USER_INFO_COOKIE)
        window.location.href = '/login'
      }, 2000)
    }, timeToTriggerLogoutInSeconds * 1000)
  }, 100) // Debounce de 100ms
}

export const InactivityWatcher: React.FC = () => {
  const { toast, dismiss } = useToast()
  const isInitializedRef = useRef(false)

  // Atualiza as funções globais quando o componente monta
  useEffect(() => {
    globalToastFunction = toast
    globalDismissFunction = dismiss
  }, [toast, dismiss])

  useEffect(() => {
    // Evita múltiplas inicializações globais
    if (globalIsInitialized || isInitializedRef.current) {
      return
    }

    globalIsInitialized = true
    isInitializedRef.current = true

    // Inicia o timer inicial
    globalResetInactivityTimer()

    // Event listeners para detectar atividade
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ]

    events.forEach((event) => {
      window.addEventListener(event, globalResetInactivityTimer, {
        passive: true,
      })
    })

    // Cleanup apenas quando o componente for desmontado
    return () => {
      isInitializedRef.current = false
      // NÃO limpa os timers globais aqui para evitar problemas de remontagem
    }
  }, [])

  return null
}
